if (typeof VMFeaturedArticles !== 'function') {
  class VMFeaturedArticles extends HTMLElement {
    constructor() {
      super();

      this.container = this.querySelector('.vm-featured-articles__container');
      this.items = this.container ? this.container.children : [];
      
      this.style.display = '';
      this.setAttribute('data-initialized', 'true');

      this.mobileDisplay = this.dataset.mobileDisplay || 'grid';
      this.mobileGridItems = parseInt(this.dataset.mobileGridItems) || 1;
      this.mobileSliderItems = parseFloat(this.dataset.mobileSliderItems) || 1;

      this.desktopDisplay = this.dataset.desktopDisplay || 'grid';
      this.desktopGridItems = parseInt(this.dataset.desktopGridItems) || 3;
      this.desktopSliderItems = parseFloat(this.dataset.desktopSliderItems) || 3;

      this.breakpoint = 768;
      this.isMobile = window.innerWidth < this.breakpoint;
      this.currentDisplay = this.isMobile ? this.mobileDisplay : this.desktopDisplay;
      this.sliderElement = null;

      this.init();
    }

    init() {
      if (!this.container || this.items.length === 0) {
        return;
      }

      this.setupLayout();
      this.setupResizeListener();
    }

    setupLayout() {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth < this.breakpoint;
      const newDisplay = this.isMobile ? this.mobileDisplay : this.desktopDisplay;

      if (this.currentDisplay !== newDisplay || wasMobile !== this.isMobile) {
        this.currentDisplay = newDisplay;
        this.destroySlider();

        if (this.currentDisplay === 'slider') {
          this.initSlider();
        } else {
          this.initGrid();
        }
      } else if (this.currentDisplay === 'grid') {
        this.updateGrid();
      } else if (this.currentDisplay === 'slider' && !this.sliderElement) {
        this.initSlider();
      }
    }

    initGrid() {
      this.container.classList.remove('vm-featured-articles--slider');
      this.container.classList.add('vm-featured-articles--grid');

      const itemsPerRow = this.isMobile ? this.mobileGridItems : this.desktopGridItems;
      this.container.style.setProperty('--grid-columns', itemsPerRow);

      Array.from(this.items).forEach((item) => {
        item.classList.remove('css-slide');
        item.style.flex = 'none';
      });
    }

    updateGrid() {
      const itemsPerRow = this.isMobile ? this.mobileGridItems : this.desktopGridItems;
      this.container.style.setProperty('--grid-columns', itemsPerRow);
    }

    initSlider() {
      if (this.items.length === 0) {
        return;
      }

      const visibleItems = this.isMobile ? this.mobileSliderItems : this.desktopSliderItems;

      if (visibleItems >= this.items.length) {
        this.initGrid();
        return;
      }

      this.container.classList.remove('vm-featured-articles--grid');
      this.container.classList.add('vm-featured-articles--slider');

      Array.from(this.items).forEach((item) => {
        item.classList.add('css-slide');
        item.style.flex = '';
        item.style.maxWidth = '';
      });

      if (!this.sliderElement) {
        const sliderOptions = {
          selector: '.css-slide',
          snapping: true,
          groupCells: false,
          navigation: true,
          navigationDOM: `<span class="css-slider-button css-slider-prev" style="display:none">←</span><span class="css-slider-button css-slider-next" style="display:none">→</span>`,
          thumbnails: true,
          thumbnailsDOM: `<div class="css-slider-dot-navigation" style="display:none"></div>`,
          indexNav: false,
          observer: true,
          disableSwipe: false,
          disableMouseDownEvent: true,
          autoplay: 0
        };

        // Wait for custom element to be defined if needed
        const createSlider = () => {
          const itemsArray = Array.from(this.items);
          this.container.innerHTML = '';

          // Build items HTML string - items need to be in innerHTML when constructor runs
          // For groupCells sliders, items should flow naturally without grid constraints
          // The slider container will handle the layout
          const itemsHTML = itemsArray.map(item => item.outerHTML).join('');

          // Create slider element using innerHTML with items included
          // This ensures constructor sees both dataset.options and items
          const tempContainer = document.createElement('div');
          tempContainer.innerHTML = `<css-slider data-options='${JSON.stringify(sliderOptions)}' class="css-slider css-slider--bottom-navigation vm-featured-articles__slider">${itemsHTML}</css-slider>`;
          this.sliderElement = tempContainer.querySelector('css-slider');

          // Append to DOM - constructor should have run with correct options and items
          this.container.appendChild(this.sliderElement);

          // Set item widths and initialize slider - use multiple attempts to ensure it works
          let setupInProgress = false;
          const setupSliderWidths = () => {
            if (setupInProgress) {
              return;
            }
            
            const viewport = this.sliderElement.querySelector('.css-slider-viewport');
            const sliderContainer = this.sliderElement.querySelector('.css-slider-container');
            
            if (!viewport || !sliderContainer) {
              // Retry if elements aren't ready yet
              setTimeout(setupSliderWidths, 10);
              return;
            }
            
            setupInProgress = true;
            
            // Get viewport width (the visible area)
            const viewportWidth = viewport.offsetWidth || this.container.offsetWidth || 1280;
            const gap = this.isMobile ? 20 : 32; // gap between items
            const itemWidth = Math.floor((viewportWidth - (gap * (visibleItems - 1))) / visibleItems);
            
            const slides = this.sliderElement.querySelectorAll('.css-slide');
            
            if (slides.length === 0) {
              setupInProgress = false;
              setTimeout(setupSliderWidths, 10);
              return;
            }
            
            slides.forEach((slide) => {
              slide.style.width = `${itemWidth}px`;
              slide.style.flexShrink = '0';
              slide.style.minWidth = `${itemWidth}px`;
            });
            
            // Ensure container expands to fit all items
            sliderContainer.style.width = 'fit-content';
            sliderContainer.style.display = 'flex';
            sliderContainer.style.gap = `${gap}px`;
            
            // Reset slider AFTER widths are set so it calculates correctly
            setTimeout(() => {
              if (this.sliderElement.resetSlider) {
                this.sliderElement.resetSlider();
              }
              if (this.sliderElement.checkSlide) {
                this.sliderElement.checkSlide();
              }
              
              setupInProgress = false;
            }, 50);
          };
          
          // Start setup - try immediately and with RAF as fallback
          setupSliderWidths();
          requestAnimationFrame(setupSliderWidths);
        };

        if (typeof customElements.get('css-slider') === 'undefined') {
          // Wait for custom element definition
          customElements.whenDefined('css-slider').then(() => {
            createSlider();
          });
        } else {
          createSlider();
        }
      }
    }

    destroySlider() {
      if (this.sliderElement) {
        if (this.sliderElement.destroySlider) {
          this.sliderElement.destroySlider();
        }

        const slides = this.sliderElement.querySelectorAll('.css-slide');
        this.container.innerHTML = '';
        slides.forEach((slide) => {
          slide.classList.remove('css-slide');
          slide.style.flex = '';
          slide.style.maxWidth = '';
          this.container.appendChild(slide);
        });

        this.sliderElement = null;
      }
    }

    setupResizeListener() {
      const debounce = (fn, wait) => {
        let t;
        return (...args) => {
          clearTimeout(t);
          t = setTimeout(() => fn.apply(this, args), wait);
        };
      };

      this.resizeHandler = debounce(() => {
        this.setupLayout();
      }, 250);

      window.addEventListener('resize', this.resizeHandler);
    }

    disconnectedCallback() {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
      }
      this.destroySlider();
    }
  }

  if (typeof customElements.get('vm-featured-articles') === 'undefined') {
    customElements.define('vm-featured-articles', VMFeaturedArticles);
  }
}

document.addEventListener('shopify:section:load', (e) => {
  if (e.target.querySelector('vm-featured-articles')) {
    const component = e.target.querySelector('vm-featured-articles');
    if (component.init) {
      component.init();
    }
  }
});

